import { useState, useCallback } from 'react';

/**
 * useToast Hook
 *
 * Manages toast notification state and provides methods to show/hide toasts.
 * Replaces alert() calls with a more polished notification system.
 */
export const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showToast = useCallback((message, type = 'info', duration = 3000) => {
    const id = Date.now() + Math.random();

    // Support both string messages and detailed error objects
    let toast;
    if (typeof message === 'object' && message.title) {
      // Detailed error object from errorHandler
      toast = {
        id,
        type: type,
        duration: message.duration || duration,
        title: message.title,
        message: message.message,
        suggestions: message.suggestions || [],
        retryable: message.retryable
      };
    } else {
      // Simple string message (backward compatible)
      toast = { id, message, type, duration };
    }

    setToasts(prev => [...prev, toast]);

    // Auto-dismiss after duration
    if (toast.duration > 0) {
      setTimeout(() => {
        dismissToast(id);
      }, toast.duration);
    }

    return id;
  }, [dismissToast]);

  const showError = useCallback((message, duration = 4000) => {
    return showToast(message, 'error', duration);
  }, [showToast]);

  const showSuccess = useCallback((message, duration = 3000) => {
    return showToast(message, 'success', duration);
  }, [showToast]);

  const showInfo = useCallback((message, duration = 3000) => {
    return showToast(message, 'info', duration);
  }, [showToast]);

  return {
    toasts,
    showToast,
    showError,
    showSuccess,
    showInfo,
    dismissToast
  };
};
